import { useTranslation } from '@dagens/frontend-i18n';
import queryString from 'query-string';
import { Navigate, useLocation } from 'react-router-dom';
import { Illustrations } from '../../../utils/Illustrations';
import SignupIntroduction from './SignupIntroduction';
import SignupConsumerFields from '_common/components/signup/SignupConsumerFields';
import SignupRequestButton from '_common/components/signup/SignupRequestButton';
import { useSignupConsumer } from '_common/hooks/useCreateRole';
import usePageView from '_common/hooks/usePageView';
import { Page } from '@components/page';
import { createConsumer, createUser } from 'api';
import {
  CONSUMER_SIGNUP_FAILED,
  CONSUMER_SIGNUP_PAGE_VIEW,
  CONSUMER_SIGNUP_SUBMITTED,
  track
} from 'utils/mixpanel';
import REQ from 'utils/REQ';

const SignupConsumers = () => {
  const [
    req,
    setReq,
    fields,
    onFieldChange,
    validation,
    validate,
    organization,
    onOrganizationChanged
  ] = useSignupConsumer();
  const { t } = useTranslation();

  usePageView(CONSUMER_SIGNUP_PAGE_VIEW);

  const { search } = useLocation();
  const { redirectLogin = '' } = queryString.parse(search);

  const onSubmit = async () => {
    if (!validate()) return;

    const {
      name,
      contactPerson,
      deliveryAddress,
      phone,
      orgNumber,
      email,
      password,
      howDidYouHearAboutUs
    } = fields;

    setReq(REQ.PENDING);
    track(CONSUMER_SIGNUP_SUBMITTED);

    try {
      const { userId } = await createUser({
        email: email.trim(),
        password,
        source: name,
        redirectLogin
      });

      await createConsumer({
        name,
        contactPerson,
        deliveryAddress,
        phone,
        orgNumber,
        email: email.trim(),
        userId,
        howDidYouHearAboutUs
      });

      setReq(REQ.SUCCESS);
    } catch (e) {
      setReq(REQ.ERROR);
      track(CONSUMER_SIGNUP_FAILED);
    }
  };

  const {
    name,
    contactPerson,
    deliveryAddress,
    phone,
    orgNumber,
    email,
    password,
    passwordRepeat,
    howDidYouHearAboutUs,
    acceptTerms
  } = fields;

  const { field: validationErrorField, message: validationErrorMessage } =
    validation;

  if (req === REQ.SUCCESS) {
    const urlParams = new URLSearchParams({
      calloutType: 'verifyEmail'
    });
    if (redirectLogin) {
      urlParams.append(
        'redirectLogin',
        encodeURIComponent(redirectLogin.toString())
      );
    }
    return <Navigate to={`/login?${urlParams.toString()}`} />;
  }

  return (
    <Page>
      <SignupIntroduction
        illustration={Illustrations.PROUD_PRODUCER_BREAD}
        header={t('signup:consumerPageHeader')}
        introductionText={t('signup:consumerPageParagraph')}
        forOtherUser={t('signup:SignUpForProducers')}
        otherUserQuery="/sell"
      />
      <form
        onSubmit={e => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <SignupConsumerFields
          name={name}
          contactPerson={contactPerson}
          deliveryAddress={deliveryAddress}
          phone={phone}
          orgNumber={orgNumber}
          email={email}
          password={password}
          passwordRepeat={passwordRepeat}
          howDidYouHearAboutUs={howDidYouHearAboutUs}
          acceptTerms={acceptTerms}
          validationErrorField={validationErrorField}
          onChange={onFieldChange}
          organization={organization}
          onOrganizationChanged={onOrganizationChanged}
        />
        <SignupRequestButton
          req={req}
          onSubmit={onSubmit}
          validationErrorMessage={validationErrorMessage}
        />
      </form>
    </Page>
  );
};

export default SignupConsumers;
